import { Component, type ReactNode, useEffect } from "react";
import { Link, useRouteError, isRouteErrorResponse } from "react-router-dom";
import { Sentry } from "../lib/sentry";
import styles from "./ErrorBoundary.module.css";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Class-based error boundary for catching render errors
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to console in development
    if (import.meta.env.DEV) {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    // Report to Sentry in all environments (if configured)
    Sentry.withScope((scope) => {
      scope.setTag("error.boundary", "class");
      scope.setContext("react", {
        componentStack: errorInfo.componentStack,
      });
      Sentry.captureException(error);
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback onReset={() => this.setState({ hasError: false, error: null })} />;
    }

    return this.props.children;
  }
}

// Route error boundary for React Router
export function RouteErrorBoundary() {
  const error = useRouteError();

  let title = "Something went wrong";
  let message = "We encountered an unexpected error. Please try again.";
  let shouldReportToSentry = true;

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = "Page not found";
      message = "The page you're looking for doesn't exist or has been moved.";
      // Don't report 404s to Sentry - they're expected navigation errors
      shouldReportToSentry = false;
    } else if (error.status === 401) {
      title = "Unauthorized";
      message = "You need to be logged in to view this page.";
      shouldReportToSentry = false;
    } else if (error.status === 403) {
      title = "Access denied";
      message = "You don't have permission to view this page.";
      shouldReportToSentry = false;
    } else if (error.status >= 500) {
      title = "Server error";
      message = "Our servers are having trouble. Please try again later.";
    }
  }

  // Report unexpected errors to Sentry
  useEffect(() => {
    if (shouldReportToSentry && error) {
      Sentry.withScope((scope) => {
        scope.setTag("error.boundary", "route");
        if (isRouteErrorResponse(error)) {
          scope.setContext("route_error", {
            status: error.status,
            statusText: error.statusText,
            data: error.data,
          });
          Sentry.captureMessage(`Route error: ${error.status} ${error.statusText}`, "error");
        } else if (error instanceof Error) {
          Sentry.captureException(error);
        } else {
          Sentry.captureMessage(`Unknown route error: ${String(error)}`, "error");
        }
      });
    }
  }, [error, shouldReportToSentry]);

  return (
    <div className={styles.errorPage}>
      <div className={styles.errorContent}>
        <div className={styles.errorIcon}>
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h1 className={styles.errorTitle}>{title}</h1>
        <p className={styles.errorMessage}>{message}</p>
        <div className={styles.errorActions}>
          <button onClick={() => window.location.reload()} className={styles.primaryButton}>
            Try again
          </button>
          <Link to="/dashboard" className={styles.secondaryButton}>
            Go to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

// Fallback component used by class-based ErrorBoundary
function ErrorFallback({ onReset }: { onReset: () => void }) {
  return (
    <div className={styles.errorPage}>
      <div className={styles.errorContent}>
        <div className={styles.errorIcon}>
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h1 className={styles.errorTitle}>Something went wrong</h1>
        <p className={styles.errorMessage}>We encountered an unexpected error. Please try again.</p>
        <div className={styles.errorActions}>
          <button onClick={onReset} className={styles.primaryButton}>
            Try again
          </button>
          <Link to="/dashboard" className={styles.secondaryButton}>
            Go to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
